import { useEffect, useRef, useState } from 'react'
import Styles from './V4CreateAndPrepare.module.scss'
import { Button } from 'blaze-ui/Button'
import { useBrandPlanSchedule } from 'hooks/queries/schedules/useBrandPlanSchedule'
import { useGenerateTheme } from 'hooks/queries/schedules/useGenerateTheme'
import { useCreateSchedule } from 'hooks/queries/brandKits/useCreateSchedule'
import { fetchBrandPlanSchedules } from 'hooks/queries/schedules/useBrandPlanSchedules'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { Strategy, buildContentStrategyTactics, description as strategyDescription } from '../StrategiesSelection/utils'
import { EmojiMap } from 'components/ContentStrategyTypeEmoji/ContentStrategyTypeEmoji'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { ContentStrategyType } from 'types'
import dayjs from 'dayjs'
import cx from 'classnames'
import { Check } from 'blaze-ui/icons/16'
import { useLoaderAnimation, scheduleHeadlines } from '../v4LoaderUtils'
import { V4LoaderArrow } from '../V4LoaderArrow'
import { TopBar } from '../TopBar/TopBar'

const getFieldCardClass = (done: boolean, active: boolean) => {
  if (done) return Styles.fieldCardDone
  if (active) return Styles.fieldCardWriting
  return Styles.fieldCardDim1
}

const STRATEGY_GRADIENTS: Partial<Record<ContentStrategyType, string>> = {
  [ContentStrategyType.LifestyleContent]:
    'linear-gradient(94deg, #0f000d 5.87%, #002a4a 22.35%, #5f00ed 45.84%, #65e8ff 75.14%)',
  [ContentStrategyType.EducationalContent]:
    'linear-gradient(94deg, #0f000d 5.97%, #002a4a 23.73%, #6d8b30 49.06%, #dccb7f 80.66%)',
  [ContentStrategyType.QuickTips]:
    'linear-gradient(92deg, #0f000d 6.27%, #02003e 28.53%, #3e3e3e 60.27%, #3e3e3e 99.87%)',
  [ContentStrategyType.ThoughtLeadership]:
    'linear-gradient(93deg, #0f000d 6.24%, #2b0000 28.54%, #dc7500 65.9%, #ffe479 100%)',
  [ContentStrategyType.ProductEducation]:
    'linear-gradient(94deg, #0f000d 5.87%, #2b0024 22.35%, #d65600 45.84%, #ffdcb6 75.14%)',
  [ContentStrategyType.ProblemSolution]:
    'linear-gradient(94deg, #0f000d 5.87%, #2b0024 22.35%, #850159 45.84%, #ffa745 75.14%)',
  [ContentStrategyType.OfferAndServices]: 'linear-gradient(95deg, #1e0019 36.76%, #d030c8 67.25%, #ff6d5f 97.73%)',
  [ContentStrategyType.OfferAndPromotion]: 'linear-gradient(95deg, #1e0019 36.76%, #d030c8 67.25%, #ff6d5f 97.73%)',
}

const PLACEHOLDER_CAMPAIGN_NAME = 'Building Trust Through Storytelling'
const PLACEHOLDER_THEME =
  'Building trust through consistent, authentic storytelling — sharing real client outcomes and behind-the-scenes moments that humanize your brand.'

const HEADLINE_PHASES = [
  { at: 0, text: 'Creating your first campaign' },
  { at: 3800, text: 'Writing your campaign theme' },
  { at: 11500, text: 'Finalising your campaign' },
]

const TW_SPEED = 38
const TW_JITTER = 9

interface Props {
  brandPlanId: number
  selectedStrategy: Strategy
  existingScheduleId: number | null
  onReady: (scheduleId: number) => void
  onBack: () => void
}

export const V4CreateAndPrepare = ({ brandPlanId, selectedStrategy, existingScheduleId, onReady, onBack }: Props) => {
  const hasStartedRef = useRef(false)
  const hasAdvancedRef = useRef(false)

  const [scheduleId, setScheduleId] = useState<number | null>(existingScheduleId)
  const [beReadyDirectId, setBeReadyDirectId] = useState<number | null>(null)
  const workspaceId = useCurrentWorkspaceId()

  const { mutateAsync: createSchedule } = useCreateSchedule({ brandPlanId })
  const { mutateAsync: generateTheme } = useGenerateTheme()

  const { data: schedule } = useBrandPlanSchedule(scheduleId ?? 0, {
    enabled: scheduleId != null,
    refetchInterval: (data) => {
      if (data === undefined || data.is_preparing || !data.themes?.length) return 1000
      return false
    },
  })

  const beReady = (schedule != null && !schedule.is_preparing && !!schedule.themes?.length) || beReadyDirectId != null

  const [nameText, setNameText] = useState('')
  const [nameDone, setNameDone] = useState(false)
  const [nameActive, setNameActive] = useState(true)
  const [themeText, setThemeText] = useState('')
  const [themeDone, setThemeDone] = useState(false)
  const [themeActive, setThemeActive] = useState(false)
  const [animationDone, setAnimationDone] = useState(false)

  const { addTimer, clearTimers, typewrite, activeHeadline, setActiveHeadline, exitHeadline, swapHeadline } =
    useLoaderAnimation(TW_SPEED, TW_JITTER)

  useEffect(() => {
    scheduleHeadlines(HEADLINE_PHASES, setActiveHeadline, swapHeadline, addTimer)

    addTimer(() => {
      typewrite(PLACEHOLDER_CAMPAIGN_NAME, setNameText, () => {
        setNameDone(true)
        setNameActive(false)
        addTimer(() => {
          setThemeActive(true)
          typewrite(PLACEHOLDER_THEME, setThemeText, () => {
            setThemeDone(true)
            setThemeActive(false)
            addTimer(() => setAnimationDone(true), 350)
          })
        }, 320)
      })
    }, 700)

    return clearTimers
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (existingScheduleId != null) {
      setScheduleId(existingScheduleId)
      return
    }

    if (hasStartedRef.current) return
    hasStartedRef.current = true

    const run = async () => {
      try {
        const existing = await fetchBrandPlanSchedules({
          workspaceId,
          brandPlanId,
          params: { page: 1, items: 1, order_direction: 'asc' },
        })
        const existingSchedule = existing.data.schedules[0]

        if (existingSchedule) {
          setScheduleId(existingSchedule.id)
          if (!existingSchedule.is_preparing && existingSchedule.themes?.length) {
            setBeReadyDirectId(existingSchedule.id)
          }
          return
        }

        const result = await createSchedule({
          prepare: false,
          start_date: dayjs().add(1, 'day').format('YYYY-MM-DD'),
          week_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          daily_frequency: 'once_a_day',
          days_before_review: 3,
          content_strategy_type: selectedStrategy.id,
          length: selectedStrategy.length,
          tactics: buildContentStrategyTactics(selectedStrategy),
          crosspost_contents: selectedStrategy.crosspost_contents,
          video_crosspost_contents: selectedStrategy.video_crosspost_contents,
          seo_contents: selectedStrategy.seo_contents,
          email_contents: selectedStrategy.email_contents,
          instagram_contents: selectedStrategy.instagram_contents,
        })

        const id = result.schedule.id
        await generateTheme({ scheduleId: id })
        setScheduleId(id)
      } catch {
        showErrorMessageToast('Something went wrong while setting up your campaign. Please try again.')
        onBack()
      }
    }

    run()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hasAdvancedRef.current) return

    const id = scheduleId ?? beReadyDirectId
    if (!id || !beReady || !animationDone) return

    hasAdvancedRef.current = true
    onReady(id)
  }, [beReady, animationDone, scheduleId, beReadyDirectId, onReady])

  const stratGradient =
    STRATEGY_GRADIENTS[selectedStrategy.id] ??
    'linear-gradient(94deg, #0f000d 5.87%, #002a4a 22.35%, #5f00ed 45.84%, #65e8ff 75.14%)'
  const stratEmoji = EmojiMap[selectedStrategy.id] ?? ''
  const stratDesc = strategyDescription[selectedStrategy.id] ?? selectedStrategy.description

  const nameCardClass = getFieldCardClass(nameDone, nameActive)
  const themeCardClass = getFieldCardClass(themeDone, themeActive)

  return (
    <div className={Styles.page}>
      <TopBar highlightStep="campaign" />
      <div className={Styles.wrap}>
        <div className={Styles.copyBlock}>
          <div className={Styles.statusRow}>
            <span>In progress</span>
            <span className={Styles.dot}>.</span>
            <span className={Styles.dot}>.</span>
            <span className={Styles.dot}>.</span>
          </div>

          <div className={Styles.headlineSlot}>
            {exitHeadline && (
              <div className={cx(Styles.headline, Styles.headlineExit)} key={`exit-${exitHeadline}`}>
                {exitHeadline}
              </div>
            )}
            <div className={cx(Styles.headline, Styles.headlineEnter)} key={`active-${activeHeadline}`}>
              {activeHeadline}
            </div>
          </div>

          <p className={Styles.subText}>
            Each campaign sets the direction for that week&apos;s content. Here&apos;s what one looks like:
          </p>
        </div>

        <hr className={Styles.divider} />

        <div className={Styles.illusRow}>
          <div className={Styles.illusCol}>
            <p className={Styles.illusLabel}>Content strategy:</p>
            <div className={Styles.stratCard}>
              <div className={Styles.stratCardHeader} style={{ background: stratGradient }}>
                {stratEmoji} {selectedStrategy.name}
              </div>
              <div className={Styles.stratCardBody}>{stratDesc}</div>
            </div>
          </div>

          <div className={Styles.arrowCol}>
            <V4LoaderArrow />
          </div>

          <div className={Styles.illusCol}>
            <p className={Styles.illusLabel}>Campaign details:</p>
            <div className={Styles.campaignCol}>
              <div className={cx(Styles.fieldCard, nameCardClass)}>
                <div className={Styles.checkSlot}>
                  <div className={cx(Styles.check, { [Styles.checkVisible]: nameDone })}>
                    <Check size={14} color="var(--green-50)" />
                  </div>
                </div>
                <p className={Styles.fieldLabel}>Campaign name</p>
                <div className={Styles.fieldText}>
                  <span className={Styles.fieldTextContent}>
                    {nameText}
                    {nameActive && !nameDone && <span className={cx(Styles.cursor, Styles.cursorOn)} />}
                  </span>
                  <span className={Styles.fieldTextReserve} aria-hidden>
                    {PLACEHOLDER_CAMPAIGN_NAME}
                  </span>
                </div>
              </div>

              <div className={cx(Styles.fieldCard, themeCardClass)}>
                <div className={Styles.checkSlot}>
                  <div className={cx(Styles.check, { [Styles.checkVisible]: themeDone })}>
                    <Check size={14} color="var(--green-50)" />
                  </div>
                </div>
                <p className={Styles.fieldLabel}>Theme</p>
                <div className={Styles.fieldText}>
                  <span className={cx(Styles.fieldTextContent, Styles.fieldTextItalic)}>
                    {themeText}
                    {themeActive && !themeDone && <span className={cx(Styles.cursor, Styles.cursorOn)} />}
                  </span>
                  <span className={cx(Styles.fieldTextReserve, Styles.fieldTextItalic)} aria-hidden>
                    {PLACEHOLDER_THEME}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={Styles.footer}>
        <div className={Styles.cancelBtn}>
          <Button variant="tertiary" size="lg" onPress={onBack}>
            Back
          </Button>
        </div>
      </div>
    </div>
  )
}
